import { defineNuxtPlugin, addRouteMiddleware, useRuntimeConfig } from "#app";
import { AccessManager } from "../services/accessManager";
import { handleRouteNavigation } from "../services/middleware";
import type { ModuleOptions } from "../types";
import { useRoles, usePermissions } from "./composables";

/**
 * Masquage réversible (lot 8, phase 3) : l'ancien `el.remove()` ne survivait pas aux
 * ré-affichages des composants PrimeVue (ContextMenu…) — Vue réinsère l'élément réutilisé
 * sans re-monter la directive, l'option réapparaissait. Le style inline, lui, reste porté
 * par l'élément réutilisé au patch. Réversible aussi : si l'accès change (permissions
 * chargées après coup), `updated` ré-affiche. Le masquage est un confort d'interface —
 * la sécurité réelle reste les policies côté API.
 */
function applyAccess(el: HTMLElement, hasAccess: boolean) {
  if (hasAccess) {
    el.style.removeProperty("display");
  }
  else {
    el.style.setProperty("display", "none", "important");
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig().public.nuxtPermissions as ModuleOptions;
  const userRoles = useRoles();
  const userPermissions = usePermissions();
  const accessManager = new AccessManager(userRoles, userPermissions, config);

  addRouteMiddleware("nuxt-permissions", (to, from) => {
    return handleRouteNavigation(to, from, accessManager, config);
  });
  createCombinedDirective(nuxtApp, accessManager);

  nuxtApp.vueApp.directive("access", {
    mounted(el, binding) {
      applyAccessBinding(el, binding, accessManager);
    },
    updated(el, binding) {
      applyAccessBinding(el, binding, accessManager);
    },
  });

  return {
    provide: {
      hasRole: (roles: string | string[]) =>
        accessManager.checkAccess(undefined, roles),
      hasPermission: (permissions: string | string[]) =>
        accessManager.checkAccess(permissions, undefined),
    },
  };
});

function applyAccessBinding(
  el: HTMLElement,
  binding: { arg?: string; value: { permissions?: string | string[]; roles?: string | string[] } },
  accessManager: AccessManager,
) {
  const { permissions, roles } = binding.value;
  const granted = accessManager.checkAccess(permissions, roles);

  applyAccess(el, binding.arg === "not" ? !granted : granted);
}

function createCombinedDirective(nuxtApp, accessManager) {
  const permissionsMap = new WeakMap();
  const rolesMap = new WeakMap();

  function updateElement(el) {
    const permissions = permissionsMap.get(el);
    const roles = rolesMap.get(el);

    applyAccess(el, accessManager.checkAccess(permissions, roles));
  }

  // Register both directives
  nuxtApp.vueApp.directive("can", {
    mounted(el, binding) {
      if (binding.arg === "not") {
        // Handle "not" case separately
        applyAccess(el, !accessManager.checkAccess(binding.value, undefined));
        return;
      }

      // Store the permissions
      permissionsMap.set(el, binding.value);
      // Check combined access
      updateElement(el);
    },
    updated(el, binding) {
      if (binding.arg === "not") {
        applyAccess(el, !accessManager.checkAccess(binding.value, undefined));
        return;
      }

      permissionsMap.set(el, binding.value);
      updateElement(el);
    },
    unmounted(el) {
      permissionsMap.delete(el);
    },
  });

  nuxtApp.vueApp.directive("role", {
    mounted(el, binding) {
      if (binding.arg === "not") {
        applyAccess(el, !accessManager.checkAccess(undefined, binding.value));
        return;
      }

      // Store the roles
      rolesMap.set(el, binding.value);
      // Check combined access
      updateElement(el);
    },
    updated(el, binding) {
      if (binding.arg === "not") {
        applyAccess(el, !accessManager.checkAccess(undefined, binding.value));
        return;
      }

      rolesMap.set(el, binding.value);
      updateElement(el);
    },
    unmounted(el) {
      rolesMap.delete(el);
    },
  });
}
